import { useStyles } from "./SetProfile.style";
import { StaticImage } from "../../components/core/StaticImage/StaticImage";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import LogoIcon3x from "../../assets/icons/logo_onmo+_3x.webp";
import { FC, HTMLAttributes, useEffect, useId, useRef, useState } from "react";
import {
  Inventory,
  InventoryAvatarData,
  Query,
  UpdateProfileInput,
} from "@onmo/graphql/graphql";
import {
  meAAAGQL,
  updateProfileGQL,
  updateUserMetadataGQL,
} from "../../graphql";
import { useMutation, useQuery } from "@apollo/client";
import { TextField } from "../../components/core/TextField/TextField";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import { getOnboardingImage } from "../../models/media";
import { Icon } from "../../components/core/Icon/Icon";
import { useAppSelector } from "../../components/hooks";
import {
  selectIsDesktopView,
  selectIsMobile,
} from "../../store/slices/appState";
import { useTranslation } from "react-i18next";
import {
  useApplyAvatar,
  useFetchFreeAvatars,
} from "../../hooks/useSelectAvatarQuery";
import { useCurrentProfile } from "../../hooks/useProfileSession";
import { useGamepadContext } from "../../contexts/GamepadProvider";
import { isValidUsernameFormat } from "../../models/regex";
import { ChooseAvatarPopup } from "../../components/common/ChooseAvatarPopup";
import Button from "../../components/core/Button/Button";

const SetProfile: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { screenSize } = useAppSelector((state) => state.appState);
  const isDesktop = useAppSelector(selectIsDesktopView);
  const isMobile = useAppSelector(selectIsMobile);
  const activateImage = getOnboardingImage(screenSize, true);

  const usernameErrorId = useId();
  const { data: dataMe, refetch } = useQuery<{ me: Query["me"] }>(meAAAGQL);
  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<Inventory>();
  const [usernameError, setUsernameError] = useState("");
  const [updateProfile] = useMutation<
    { updateProfile: { message: string } },
    { input: UpdateProfileInput }
  >(updateProfileGQL);
  const [updateUserMetadata] = useMutation(updateUserMetadataGQL);

  const { freeAvatars: avatars } = useFetchFreeAvatars();
  const { currentProfile: profile } = useCurrentProfile();
  const applyAvatar = useApplyAvatar();
  const { setCallback, cleanup, setActiveElement } = useGamepadContext();
  const avatarButtonRef = useRef<HTMLDivElement>(null);
  const [isAvatarOpen, setIsAvatarOpen] = useState(false);

  const closeAvatarPopup = () => {
    setIsAvatarOpen(false);
    if (avatarButtonRef.current) setActiveElement(avatarButtonRef.current);
  };

  const onSaveAvatar = (inventoryId: string) => {
    const avatar = avatars.find((item) => item.id === inventoryId);
    if (avatar) setSelectedAvatar(avatar as unknown as Inventory);
    closeAvatarPopup();
  };

  const validateUsername = (username: string): string => {
    if (username.length < 3 || username.length > 16) {
      return t("SetProfile_Username must be between 3-16 characters long.");
    }

    if (!isValidUsernameFormat(username)) {
      return t("SetProfile_Username only allows letters, numbers, _ and -.");
    }

    return "";
  };

  const onSubmit = async () => {
    const trimmedUsername = username.trim();
    const error = validateUsername(trimmedUsername);
    if (error) {
      setUsernameError(error);
      return;
    }

    if (isSubmitting) return;
    try {
      setIsSubmitting(true);

      if (!selectedAvatar) return;
      AnalyticsGA4.profileSetup(selectedAvatar.title);

      const currentOnboarding = JSON.parse(
        dataMe?.me?.metadata?.onboarding || "{}",
      );
      const updatedOnboarding = {
        ...currentOnboarding,
        hasCheckedUserNameAvatar: true,
      };

      // Sequential: BE races creating the main profile on parallel calls
      if (profile) {
        await updateProfile({
          variables: {
            input: {
              id: profile.id,
              action: { editDetails: { username: trimmedUsername } },
            },
          },
          // updateProfile returns only a message
          update: (cache) => {
            cache.modify({
              id: cache.identify({ __typename: "Profile", id: profile.id }),
              fields: { username: () => trimmedUsername },
            });
          },
        });
        await applyAvatar(selectedAvatar, profile.id);
      }
      await updateUserMetadata({
        variables: {
          input: { onboarding: JSON.stringify(updatedOnboarding) },
        },
        update: (cache) => {
          const existingMeData = cache.readQuery<{ me: Query["me"] }>({
            query: meAAAGQL,
          });
          if (existingMeData?.me) {
            cache.writeQuery({
              query: meAAAGQL,
              data: {
                me: {
                  ...existingMeData.me,
                  metadata: {
                    ...existingMeData.me.metadata,
                    onboarding: JSON.stringify(updatedOnboarding),
                  },
                },
              },
            });
          }
        },
      });
      await refetch();
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const initialUsername = profile?.name || dataMe?.me.username;
  useEffect(() => {
    if (initialUsername) setUsername(initialUsername);
  }, [initialUsername]);

  useEffect(() => {
    if (avatars.length > 0 && !selectedAvatar) {
      setSelectedAvatar(avatars?.[0] as unknown as Inventory);
    }
  }, [avatars, selectedAvatar]);

  useEffect(() => {
    setCallback({
      onBackAction: async () => {
        await onSubmit();
      },
    });
    return () => cleanup();
  }, [setCallback, cleanup, username, selectedAvatar]);

  return (
    <div className={classes.root}>
      <StaticImage
        alt=""
        src={activateImage.bgSrc}
        className={classes.background}
      />
      <div className={classes.container}>
        <StaticImage
          alt=""
          src={activateImage?.src1x}
          src2x={activateImage?.src2x}
          src3x={activateImage?.src3x}
          className={classes.deviceImage}
        />
        <div className={classes.content}>
          <StaticImage src={LogoIcon3x} alt="ONMO+" className={classes.logo} />
          <Typography
            variant={
              isMobile
                ? TypographyVariant.HeadingH5Bold
                : isDesktop
                ? TypographyVariant.HeadingH3Bold
                : TypographyVariant.HeadingH3Bold
            }
          >
            {t("SetProfile_Create Your Account")}
          </Typography>
          <Typography
            color={TypographyColor.Silver}
            variant={
              isDesktop
                ? TypographyVariant.BodyB3Medium
                : TypographyVariant.BodyB5Medium
            }
            className={classes.description}
          >
            {t(
              "SetProfile_Enter a profile name and pick an avatar to get started!",
            )}
          </Typography>
          <div className={classes.inputContainer}>
            <div
              ref={avatarButtonRef}
              className={classes.avatarButton}
              role="button"
              aria-label={t("ProfileInfoPanel_Pick an avatar")}
              onClick={() => setIsAvatarOpen(true)}
              data-gamepad="avatar-select"
              data-selected-class={classes.avatarButtonSelected}
            >
              {selectedAvatar && (
                <StaticImage
                  alt=""
                  className={classes.avatar}
                  src={(selectedAvatar.data as InventoryAvatarData)?.url}
                />
              )}
              <span className={classes.avatarBadge}>
                <Icon name="icon-bold-edit-2" size={isDesktop ? 32 : 24} />
              </span>
            </div>
            <div className={classes.inputName}>
              <Typography
                color={TypographyColor.Silver}
                variant={
                  isDesktop
                    ? TypographyVariant.BodyB3Medium
                    : TypographyVariant.BodyB5Medium
                }
                className={classes.profileName}
              >
                {t("ProfileForm_Profile Name")}
              </Typography>
              <TextField
                fullWidth
                inputProps={{
                  placeholder: t("SetProfile_Enter profile name"),
                  "aria-label": t("ProfileInfoPanel_Username"),
                  "aria-describedby": usernameError
                    ? usernameErrorId
                    : undefined,
                  "aria-invalid": !!usernameError || undefined,
                  value: username,
                  dataGamepad: "input-username",
                }}
                aria-describedby={usernameError ? usernameErrorId : undefined}
                isShowClear
                onChangeValue={(value) => {
                  setUsername(value);
                  if (usernameError) setUsernameError("");
                }}
                isError={!!usernameError}
              />
              {usernameError && (
                <Typography
                  id={usernameErrorId}
                  role="alert"
                  variant={TypographyVariant.BodyB6Regular}
                  color={TypographyColor.Error}
                  className={classes.warning}
                >
                  <Icon
                    size={13}
                    name="icon-bold-info-circle"
                    className={classes.warningIcon}
                  />
                  {usernameError}
                </Typography>
              )}
            </div>
          </div>
          <Button
            fullWidth
            size={isDesktop ? "large" : "extraLarge"}
            disabled={!username || !selectedAvatar || isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            {t("SetProfile_Continue")}
          </Button>
        </div>
      </div>
      {isAvatarOpen && (
        <ChooseAvatarPopup
          open
          selectedId={selectedAvatar?.id}
          onSave={onSaveAvatar}
          onClose={closeAvatarPopup}
        />
      )}
    </div>
  );
};

export default SetProfile;
